import { useState } from "react";
import axios from "axios";
import { X, Check, Loader2, Crown, Sparkles, Lock } from "lucide-react";
import { errMsg } from "./paywall";

const FREE_FEATURES = [
  "1 subject · 5 teacher replies a day",
  "Tap-to-listen voice · 10 min/day",
  "1 practice quiz a day",
  "Streaks & badges",
];

function loadCashfree(cb) {
  if (window.Cashfree) { cb(); return; }
  const s = document.createElement("script");
  s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
  s.onload = cb;
  document.body.appendChild(s);
}

const ACCENT = { free: "#71717a", tutor_plus: "#22D3EE", tutor_pro: "#FBBF24" };

export const TutorPlans = ({ api, authConfig, entitlement, plans, reason, onClose }) => {
  const [cycle, setCycle] = useState("annual");
  const [loading, setLoading] = useState("");
  const [err, setErr] = useState("");
  const tier = entitlement?.tier || "free";
  const isSub = entitlement?.source === "subscription";

  async function buy(plan) {
    setErr(""); setLoading(plan.id);
    try {
      const r = await axios.post(`${api}/subscriptions/create-order`, {
        plan_id: plan.id, billing_cycle: cycle, origin_url: window.location.origin, return_path: "/tutor",
      }, authConfig);
      const { payment_session_id } = r.data;
      loadCashfree(() => {
        window.Cashfree({ mode: "production" }).checkout({ paymentSessionId: payment_session_id, redirectTarget: "_self" });
      });
    } catch (e) {
      setErr(errMsg(e, "Could not start payment."));
      setLoading("");
    }
  }

  const cards = [
    { id: "free", name: "Free", monthly_price: 0, annual_price: 0, features: FREE_FEATURES, tierKey: "free" },
    ...(plans || []).map((p) => ({ ...p, tierKey: p.id === "tutor_pro" ? "pro" : "plus" })),
  ];

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-start sm:items-center justify-center overflow-y-auto px-3 py-6" data-testid="tutor-plans-modal">
      <div className="w-full max-w-4xl bg-[#0A0A0A] border border-zinc-800 rounded-3xl p-5 sm:p-8 relative">
        <button onClick={onClose} data-testid="plans-close-btn" className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X size={18}/></button>
        {reason && (
          <div className="mb-5 px-4 py-3 rounded-2xl bg-amber-500/10 border border-amber-400/30 text-amber-200 text-sm flex items-start gap-2" data-testid="paywall-reason">
            <Lock size={14} className="mt-0.5 shrink-0"/> <span>{reason}</span>
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <p className="text-cyan-300 text-xs font-bold uppercase tracking-wider mb-1 flex items-center gap-2"><Sparkles size={12}/> ShansoTutor plans</p>
            <h3 className="text-white font-black text-2xl">Less than one tuition class a month</h3>
            <p className="text-zinc-500 text-sm mt-1">Cancel anytime · UPI, cards & netbanking via Cashfree · GST included</p>
          </div>
          <div className="inline-flex bg-zinc-900 border border-zinc-800 rounded-full p-1 self-start" data-testid="plans-cycle-toggle">
            {["monthly", "annual"].map((c) => (
              <button key={c} onClick={() => setCycle(c)} data-testid={`plans-cycle-${c}`}
                className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${cycle === c ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}>
                {c === "monthly" ? "Monthly" : "Annual · save 25%"}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {cards.map((p) => {
            const current = tier === p.tierKey && (p.tierKey === "free" ? !isSub && entitlement?.source !== "trial" : true);
            const price = cycle === "annual" ? p.annual_price : p.monthly_price;
            const accent = ACCENT[p.id];
            const recommended = p.id === "tutor_plus";
            return (
              <div key={p.id} data-testid={`plan-card-${p.id}`}
                className={`rounded-2xl border p-5 flex flex-col relative ${recommended ? "border-cyan-400/60 bg-cyan-500/5" : "border-zinc-800 bg-[#0d0d0d]"}`}>
                {recommended && <span className="absolute -top-2.5 left-5 text-[10px] font-black bg-cyan-400 text-black px-2 py-0.5 rounded-full">MOST POPULAR</span>}
                <div className="flex items-center gap-2 mb-3">
                  {p.id === "tutor_pro" && <Crown size={15} style={{ color: accent }}/>}
                  <p className="font-bold text-white">{p.name.replace("ShansoTutor ", "")}</p>
                  {current && <span className="ml-auto text-[10px] font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-400/30 px-2 py-0.5 rounded-full" data-testid={`plan-current-${p.id}`}>Current</span>}
                </div>
                <div className="mb-4">
                  <span className="text-3xl font-black text-white">₹{price.toLocaleString("en-IN")}</span>
                  {price > 0 && <span className="text-zinc-500 text-xs ml-1">/{cycle === "annual" ? "year" : "month"}</span>}
                  {price > 0 && cycle === "annual" && <p className="text-zinc-500 text-[11px] mt-0.5">≈ ₹{Math.round(price / 12)}/month</p>}
                </div>
                <ul className="space-y-2 mb-5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-zinc-300"><Check size={13} className="mt-0.5 shrink-0" style={{ color: accent }}/> {f}</li>
                  ))}
                </ul>
                {p.id === "free" ? (
                  <div className="text-center text-xs text-zinc-500 py-2.5">Always free</div>
                ) : (
                  <button onClick={() => buy(p)} disabled={!!loading || (current && isSub)} data-testid={`plan-buy-${p.id}`}
                    className="w-full py-2.5 rounded-full text-sm font-bold text-black disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ background: p.id === "tutor_pro" ? "linear-gradient(135deg,#FBBF24,#F59E0B)" : "linear-gradient(135deg,#22D3EE,#06B6D4)" }}>
                    {loading === p.id ? <Loader2 size={14} className="animate-spin"/> : null}
                    {current && isSub ? "Active" : `Get ${p.name.replace("ShansoTutor ", "")}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {err && <p className="text-red-400 text-xs mt-4" data-testid="plans-error">{err}</p>}
        {entitlement?.source === "trial" && (
          <p className="text-zinc-500 text-xs mt-4" data-testid="plans-trial-note">
            You're on the free {entitlement.trial_days_left}-day Plus trial — everything in Plus is unlocked until it ends.
          </p>
        )}
      </div>
    </div>
  );
};
